import { useCallback, useEffect, useState, useMemo } from 'react';
import clsx from 'clsx';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { Button, Text, size, tags, useCheckBreakpoint, canUseDOM } from '@marriott/mi-ui-library';
import { useRouter } from 'next/router';
import { useStore } from '../../modules/store/cobrandStore';
import { COBRANDSESSIONDATA, CREDITCARDENDPOINT } from '../../modules/constants';
import { isMobile } from '../../modules/utils';
import { CtaComponentTypes } from './CtaComponent.types';
import { StyledCompareButton } from './CtaComponent.styles';

export const CtaComponentConfig = {
  emptyLabel: 'Cta Component',
  isEmpty: () => true,
  resourceType: `/components/content/cta-component`,
};

export const CtaComp = (props: CtaComponentTypes) => {
  const {
    openInNewTab,
    linkText,
    linkUrl,
    ctaDescriptionMobile,
    ctaDescriptionDesktop,
    enableButtonByDefault,
    targetExperience,
    trackingProperties,
    comparisonTrayFlag,
    hideCtaDescription,
  } = props;

  const linkType = openInNewTab ? 'external' : 'internal';
  const clickTrackValue = `${trackingProperties?.trackingContentPosition}|${trackingProperties?.trackingDescription}|${linkType}|${trackingProperties?.additionalTrackingVariables}`;
  const enableCTAButton = enableButtonByDefault === 'true';
  const ENABLE_COMPARISON_TRAY = String(props?.isComparisonTrayEnabled) === 'true';
  const { cobrandData, setCobrandData } = useStore(store => store);
  const [compareButtonUrl, setCompareButtonUrl] = useState<string>('');
  const [compareButtonText, setCompareButtonText] = useState<string>('');

  const isViewportL = useCheckBreakpoint('viewportL');
  const isMobileDevice = isMobile();
  const router = useRouter();

  // Utility to build URL params
  const buildUrlParams = useCallback(
    (cardParams: string) => {
      const params = new URLSearchParams();
      cardParams
        .split('&')
        .filter(Boolean)
        .forEach(param => {
          const [key, value] = param.split('=');
          if (key && value) params.set(key, value);
        });
      if (targetExperience) params.set('experience', targetExperience);
      if (router?.query) {
        Object.entries(router.query).forEach(([key, value]) => {
          params.set(key, value as string);
        });
      }
      return `?${params.toString()}`;
    },
    [router.query, targetExperience]
  );

  // Session storage logic
  const setSelections = useCallback(() => {
    if (canUseDOM) {
      sessionStorage?.setItem(COBRANDSESSIONDATA, JSON.stringify(cobrandData));
      sessionStorage?.setItem(CREDITCARDENDPOINT, JSON.stringify(router?.asPath));
    }
  }, [cobrandData, router?.asPath]);

  // Effect: restore session data
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const cobrandDataFromSessionStorage = sessionStorage?.getItem(COBRANDSESSIONDATA);
      if (cobrandDataFromSessionStorage) {
        setCobrandData(JSON.parse(cobrandDataFromSessionStorage));
        sessionStorage.removeItem(COBRANDSESSIONDATA);
      }
      sessionStorage?.removeItem(CREDITCARDENDPOINT);
    }
  }, [setCobrandData]);

  // Effect: build compare button URL for all carousel cards
  useEffect(() => {
    if (cobrandData?.allCarouselCards?.length) {
      const cardParams = cobrandData.allCarouselCards.map((element, i) => `cardtype${i + 1}=${element}`).join('&');
      setCompareButtonUrl(buildUrlParams(cardParams));
    }
  }, [cobrandData?.allCarouselCards, buildUrlParams]);

  // Memo: get compare URL for selected cards
  const getCardCompareUrl = useCallback(() => {
    const cardParams = cobrandData.selectedCards
      .filter(card => card.checked)
      .map((element, i) => `cardtype${i + 1}=${element.cardname}`)
      .join('&');
    return buildUrlParams(cardParams);
  }, [cobrandData.selectedCards, buildUrlParams]);

  // Memo: enable card comparison and button text
  const { enableCardComparison, checkedCount } = useMemo(() => {
    const checked = cobrandData?.selectedCards?.filter(card => card?.checked) || [];
    return {
      enableCardComparison: checked.length >= 2,
      checkedCount: checked.length,
    };
  }, [cobrandData?.selectedCards]);

  useEffect(() => {
    setCompareButtonText(checkedCount > 0 ? ` (${checkedCount})` : '');
  }, [checkedCount]);

  // Button disabled logic
  const isButtonDisabled = !enableCardComparison && !enableCTAButton;

  // Render logic inside return statement
  return (
    <StyledCompareButton data-testid="m-cta-component" data-component-name="m-cobrand-cta-component">
      {/* 
      enableCTAButton : Enables button by default, should be used in pages where comparison checkbox is not displayed in cardoverview components [Japan FOC]
      ENABLE_COMPARISON_TRAY : Displays comparison tray, Compare button to be displayed when tray functionality is disabled [ UK FOC]
      comparisonTrayFlag : only true when compare button is called inside comparison tray component. [ US FOC with comparison tray enabled]
       */}
      {(enableCTAButton || !ENABLE_COMPARISON_TRAY || (ENABLE_COMPARISON_TRAY && comparisonTrayFlag)) && (
        <div className="col-12 d-flex justify-content-center mt-3">
          <div className="text-center">
            {ctaDescriptionDesktop && !ENABLE_COMPARISON_TRAY && !hideCtaDescription && (
              <Text
                fontSize={size.medium}
                element={tags.paragraph}
                copyText={isViewportL && !isMobileDevice ? ctaDescriptionDesktop : ctaDescriptionMobile}
                customClass={'t-subtitle-m  mb-3'}
              />
            )}
            {linkText && enableCTAButton && (
              <Button
                className={clsx('m-button-l compare-button mx-auto px-4')}
                isLink={true}
                href={linkUrl + compareButtonUrl}
                buttonCopy={ENABLE_COMPARISON_TRAY ? linkText : linkText + compareButtonText}
                target={openInNewTab === 'true' ? '_blank' : '_self'}
                linkType={linkType}
                trackingProperties={trackingProperties}
                callback={setSelections}
                testId="comparebtn"
                custom_click_track_value={clickTrackValue}
              />
            )}
            {linkText && !enableCTAButton && (
              <Button
                className={clsx('m-button-l compare-button mx-auto px-4', isButtonDisabled && 'disabled')}
                isLink={true}
                href={linkUrl + getCardCompareUrl()}
                buttonCopy={ENABLE_COMPARISON_TRAY ? linkText : linkText + compareButtonText}
                target={openInNewTab === 'true' ? '_blank' : '_self'}
                linkType={linkType}
                isDisabled={isButtonDisabled}
                trackingProperties={{
                  ...trackingProperties,
                }}
                callback={setSelections}
                testId="comparebtn2"
                custom_click_track_value={clickTrackValue}
              />
            )}
          </div>
        </div>
      )}
    </StyledCompareButton>
  );
};

export const CtaComponent = (props: CtaComponentTypes) => (
  <EditableComponent config={CtaComponentConfig} {...props}>
    <CtaComp {...props} />
  </EditableComponent>
);
